// @ts-nocheck
/**
 * 🚀 V143.1 PRODUCTION CONSOLE CLEANUP - Smart Environment-Aware Logging
 * Clean, professional logging system for production deployment
 * - Development: Full logging with emojis and details
 * - Production: Full logging enabled (no filtering)
 *
 * V167.DISCONNECT: All logs enabled for debugging disconnect issues
 */

const isDev = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

// V167.DISCONNECT: Always enable logs (no filtering)
const ALWAYS_ENABLE_LOGS = true;

// V143.1: Enhanced throttling with smart message categorization
const messageThrottle = new Map<string, number>();
const THROTTLE_DURATION = 10000; // 10 seconds

const shouldThrottle = (key: string): boolean => {
  const now = Date.now();
  const lastLogged = messageThrottle.get(key);
  
  if (!lastLogged || now - lastLogged > THROTTLE_DURATION) {
    messageThrottle.set(key, now);
    return false;
  }
  return true;
};

// V143.1: Production-optimized logger with zero debug pollution
export const logger = {
  /**
   * Debug logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  debug: (...args: any[]) => {
    console.log('🐛 [DEBUG]', ...args);
  },

  /**
   * Info logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  info: (...args: any[]) => {
    console.log('ℹ️ [INFO]', ...args);
  },

  /**
   * Success logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  success: (...args: any[]) => {
    console.log('✅ [SUCCESS]', ...args);
  },

  /**
   * Test logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  test: (...args: any[]) => {
    console.log('🧪 [TEST]', ...args);
  },

  /**
   * Warning logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  warn: (...args: any[]) => {
    // Suppress non-critical warnings even in dev mode
    const message = args[0];
    if (typeof message === 'string' && message.includes('V99.25: INSTANT ACCESS')) {
      return; // Suppress InstantLoadingService warnings
    }
    console.warn('⚠️ [WARN]', ...args);
  },

  /**
   * Error logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  error: (...args: any[]) => {
    console.error('❌ [ERROR]', ...args);
  },

  /**
   * Performance logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  perf: (message: string, duration?: number) => {
    const emoji = duration && duration < 100 ? '⚡' : duration && duration < 500 ? '🚀' : '🐌';
    console.log(`${emoji} [PERF] ${message}${duration ? ` (${duration}ms)` : ''}`);
  },

  /**
   * V143.1: Throttled debug logs to reduce console spam
   * V167.DISCONNECT: Throttling disabled for debugging
   */
  debugThrottled: (key: string, ...args: any[]) => {
    // Always log (throttling disabled for debugging)
    console.log('🔍 [DEBUG-THROTTLED]', ...args);
  },

  /**
   * Network logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  network: (message: string, ...args: any[]) => {
    console.log('🌐 [NETWORK]', message, ...args);
  },

  /**
   * V143.1: Component lifecycle logs - Always enabled (no filtering)
   * V167.DISCONNECT: All logs enabled for debugging
   */
  component: (name: string, action: string, ...args: any[]) => {
    console.log(`🧩 [${name.toUpperCase()}] ${action}`, ...args);
  }
};

// Legacy support - redirect old productionLog calls
export const productionLog = {
  info: logger.info,
  warn: logger.warn,
  error: logger.error,
  debug: logger.debug
};

// Export as default for easy replacement of console.log
export default logger;

